import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Camera, Users, ChevronRight, Heart, GraduationCap } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const CLASSES = [
  {
    name: "KG 1", motto: "Little Stars, Big Dreams",
    teacher: "[Teacher Name]", students: 22,
    photo: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&q=70",
    bios: [
      { name: "Ama Darling", quote: "I love painting and playing with my friends!" },
      { name: "Kofi Junior", quote: "When I grow up I want to be a pilot." },
      { name: "Efua Grace", quote: "My favourite subject is reading time." },
    ]
  },
  {
    name: "KG 2", motto: "Growing Together, Learning Forever",
    teacher: "[Teacher Name]", students: 24,
    photo: "https://images.unsplash.com/photo-1587654780179-95c2b4b22f76?w=400&q=70",
    bios: [
      { name: "Yaw Bright", quote: "I can count to one hundred!" },
      { name: "Adwoa Pearl", quote: "I love singing in class." },
      { name: "Kwame Lion", quote: "My best friend is everyone." },
    ]
  },
  {
    name: "Class 1", motto: "First Steps to Greatness",
    teacher: "[Teacher Name]", students: 28,
    photo: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400&q=70",
    bios: [
      { name: "Priscilla Joy", quote: "Mathematics is my superpower!" },
      { name: "Emmanuel Star", quote: "I love science experiments." },
      { name: "Abena Hope", quote: "Books are my best friends." },
    ]
  },
  {
    name: "Class 2", motto: "Building Blocks of Knowledge",
    teacher: "[Teacher Name]", students: 30,
    photo: "https://images.unsplash.com/photo-1577896851231-70ef18881754?w=400&q=70",
    bios: [
      { name: "Daniel Wisdom", quote: "I want to become a doctor." },
      { name: "Grace Amara", quote: "I love our school garden." },
    ]
  },
  {
    name: "Class 3", motto: "Exploring New Horizons",
    teacher: "[Teacher Name]", students: 27,
    photo: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&q=70",
    bios: [
      { name: "Samuel Victory", quote: "Coding is the future!" },
      { name: "Mercy Light", quote: "I won the spelling bee!" },
    ]
  },
  {
    name: "Class 4", motto: "Reaching for the Stars",
    teacher: "[Teacher Name]", students: 29,
    photo: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=400&q=70",
    bios: [
      { name: "Nana Courage", quote: "Football and fractions are my thing." },
      { name: "Esi Precious", quote: "I love reading African folktales." },
    ]
  },
  {
    name: "Class 5", motto: "Champions in the Making",
    teacher: "[Teacher Name]", students: 26,
    photo: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&q=70",
    bios: [
      { name: "Adwoa Brilliant", quote: "Math Olympiad gold medallist!" },
      { name: "Kofi Strength", quote: "I dream of being an engineer." },
    ]
  },
  {
    name: "Class 6", motto: "Leaders of Tomorrow",
    teacher: "[Teacher Name]", students: 31,
    photo: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400&q=70",
    bios: [
      { name: "Ama Royalty", quote: "Athletics champion and proud!" },
      { name: "Yaw Legend", quote: "Preparing for JHS, I'm ready!" },
    ]
  },
  {
    name: "JHS 1", motto: "New Beginnings, Greater Heights",
    teacher: "[Form Master]", students: 35,
    photo: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=400&q=70",
    bios: [
      { name: "Efua Phoenix", quote: "JHS is exciting — so many new subjects!" },
      { name: "Daniel Thunder", quote: "Science lab is the best!" },
    ]
  },
  {
    name: "JHS 2", motto: "Forging Ahead with Purpose",
    teacher: "[Form Mistress]", students: 33,
    photo: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=70",
    bios: [
      { name: "Kwame Future", quote: "National Science Quiz team member!" },
      { name: "Akua Diamond", quote: "Head girl aspirant — watch this space." },
    ]
  },
  {
    name: "JHS 3", motto: "The Finish Line Awaits",
    teacher: "[Form Master]", students: 32,
    photo: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&q=70",
    bios: [
      { name: "Akua Mensah", quote: "BECE ready! Straight 1s loading..." },
      { name: "Efua Owusu", quote: "Proud Head Girl of YCPS 2025/2026." },
      { name: "Nana Yeboah", quote: "Captain of the football team — YCPS forever!" },
    ]
  },
];

const YearbookPage = () => {
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="yearbook-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <button onClick={() => { selectedClass !== null ? setSelectedClass(null) : navigate(-1); }}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> {selectedClass !== null ? "All Classes" : "Back"}
          </button>
          <div className="flex items-center gap-3">
            <BookOpen size={28} />
            <div>
              <h1 className="text-2xl font-bold">School Yearbook 2025-2026</h1>
              <p className="text-white/70 text-sm">Young Christian Preparatory School / JHS — Memories to Cherish</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {selectedClass === null ? (
          <div className="space-y-6">
            <p className="text-center text-[#002147]/70 text-sm">Click a class to view their yearbook page</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {CLASSES.map((cls, i) => (
                <button key={i} onClick={() => setSelectedClass(i)}
                  className={`text-left bg-white rounded-2xl overflow-hidden ${btn3D} cursor-pointer select-none group`}
                  data-testid={`class-card-${i}`}>
                  <div className="h-40 overflow-hidden relative">
                    <img src={cls.photo} alt={cls.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3">
                      <h3 className="font-bold text-white text-lg">{cls.name}</h3>
                      <p className="text-white/70 text-xs italic">"{cls.motto}"</p>
                    </div>
                    <Badge className="absolute top-3 right-3 bg-white/20 text-white">{cls.students} students</Badge>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600"><strong>Teacher:</strong> {cls.teacher}</p>
                    <p className="text-xs text-[#FF6B00] font-medium mt-2 flex items-center gap-1">View Class Page <ChevronRight size={12} /></p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ClassPage cls={CLASSES[selectedClass]} />
        )}
      </div>
    </div>
  );
};

const ClassPage = ({ cls }) => (
  <div className="space-y-6" data-testid="class-detail">
    {/* Class Hero */}
    <Card className="overflow-hidden border-0 shadow-2xl">
      <div className="relative h-56">
        <img src={cls.photo} alt={cls.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#002147] via-[#002147]/40 to-transparent" />
        <div className="absolute bottom-4 left-6 right-6">
          <Badge className="bg-[#FF6B00] text-white mb-2">{cls.students} Students</Badge>
          <h2 className="text-3xl font-bold text-white">{cls.name}</h2>
          <p className="text-white/70 italic">"{cls.motto}"</p>
          <p className="text-white/50 text-sm mt-1"><GraduationCap size={12} className="inline mr-1" /> Form Teacher: {cls.teacher}</p>
        </div>
      </div>
    </Card>

    {/* Student Bios */}
    <h3 className="text-lg font-bold text-[#002147] flex items-center gap-2"><Heart size={18} className="text-pink-500" /> Student Spotlights</h3>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cls.bios.map((bio, i) => (
        <Card key={i} className="hover:shadow-lg transition-shadow" data-testid={`bio-${i}`}>
          <CardContent className="p-5">
            <div className="w-12 h-12 bg-gradient-to-br from-[#002147] to-[#0a3d7a] rounded-full flex items-center justify-center text-white font-bold text-sm mb-3 shadow-md">
              {bio.name.split(" ").map(n => n[0]).join("")}
            </div>
            <h4 className="font-semibold text-[#002147]">{bio.name}</h4>
            <p className="text-sm text-gray-600 mt-1 italic">"{bio.quote}"</p>
          </CardContent>
        </Card>
      ))}
    </div>

    {/* Class Photo Placeholder */}
    <Card className="border-dashed border-2 border-[#002147]/20">
      <CardContent className="p-8 text-center">
        <Camera size={40} className="mx-auto text-[#002147]/20 mb-3" />
        <p className="text-[#002147]/50 font-medium">Class Group Photo Coming Soon</p>
        <p className="text-xs text-gray-400 mt-1">Photos will be uploaded after the school photo day</p>
      </CardContent>
    </Card>
  </div>
);

export default YearbookPage;
